import { useCallback, useEffect } from "react";
import { Address, CreateAddress, UpdateAddress } from "../types/models/Address.d";
import { UpdateEntityAddress } from "../types/models/Address";
import { useGetAddressListQuery, useInsertAddressMutation, useUpdateAddressMutation, useDeleteAddressMutation, } from "../apis/Address.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useAddress(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertAddressMutation, { 
        isLoading: insertAddressLoading, 
        isSuccess: insertAddressIsSuccess,
        isError: insertAddressIsError,
        error: insertAddressError,
    }] = useInsertAddressMutation()
    const [updateAddressMutation, { 
        isLoading: updateAddressLoading,
        isSuccess: updateAddressIsSuccess,
        isError: updateAddressIsError,
        error: updateAddressError,
    }] = useUpdateAddressMutation()
    const [deleteAddressMutation, { 
        isLoading: deleteAddressLoading,
        isSuccess: deleteAddressIsSuccess,
        isError: deleteAddressIsError,
        error: deleteAddressError,
    }] = useDeleteAddressMutation()

    const loading =
        insertAddressLoading ||
        updateAddressLoading ||
        deleteAddressLoading ||
        false

    const insertAddress = useCallback(async (address: CreateAddress, redirectPath: string|undefined) => {
        try {
            const data = await insertAddressMutation(address).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertAddressMutation, navigate])
    
    useEffect(() => {
        if (insertAddressIsSuccess) {
            openSuccessAlert('Address created')
        } else if (insertAddressIsError) {
            console.log("add error", insertAddressError)
            openFailAlert(t`Address add error: ` + insertAddressError?.data?.message)
        }
    }, [
        insertAddressIsSuccess,
        insertAddressIsError,
        insertAddressError
    ]);

    const updateAddress = useCallback(async (address: UpdateEntityAddress, redirectPath: string|undefined) => {
        try {
            const data = await updateAddressMutation(address).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateAddressMutation, navigate])
    
    useEffect(() => {
        if (updateAddressIsSuccess) {
            openSuccessAlert(t`Address updated`)
        } else if (updateAddressIsError) {
            console.log("update error", updateAddressError)
            openFailAlert(t`Address update error: ` + updateAddressError?.data?.message)
        }
    }, [
        updateAddressIsSuccess,
        updateAddressIsError,
        updateAddressError
    ]);

    const deleteAddress = useCallback((address: Address, redirectPath: string|undefined) => {
        openDialog(async () => {
            address = {
                ...address,
            }
            try {
                const { data } = await deleteAddressMutation(address);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteAddressMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteAddressIsSuccess) {
            openSuccessAlert(t`Address deleted`)
        } else if (deleteAddressIsError) {
            console.log("delete error", deleteAddressError)
            openFailAlert(t`Address delete error: ` + deleteAddressError?.data?.message)
        }
    }, [
        deleteAddressIsSuccess,
        deleteAddressIsError,
        deleteAddressError
    ]);

    return {
        loading,
        insertAddress,
        updateAddress,
        deleteAddress
    }
}
